import type { WorldTileId } from "../world/schema/tile.ts";
import { GLASS_VILNIAUS_COURTYARD_PASSAGE_VALUES } from "./glassVilniausPassages.generated.ts";
import { RULERS_WAY_COURTYARD_PASSAGE_VALUES } from "./rulersWayPassages.generated.ts";

export interface AuthoredBuildingPassageSpec {
  readonly id: string;
  /** Visible authored arch that this physics opening supports. */
  readonly visiblePassageId?: string;
  readonly tileId: WorldTileId;
  readonly buildingId: string;
  /** Source-road centreline in metres relative to the owning tile origin. */
  readonly centreLineLocalM: Readonly<{
    from: Readonly<{ x: number; z: number }>;
    to: Readonly<{ x: number; z: number }>;
  }>;
  readonly widthM: number;
  /** Height of the tunnel soffit above the source building base. */
  readonly ceilingOffsetFromBuildingBaseM: number;
  /** When present, the passage runs from the outer wall into this courtyard hole. */
  readonly destinationHoleIndex?: number;
  /** Opens a coincident source wall without constructing a second tunnel lining. */
  readonly supplementalSharedWall?: boolean;
}

export const BUILDING_PASSAGE_LINING_THICKNESS_M = 0.34;
export const BUILDING_PASSAGE_LINING_DEPTH_BELOW_BASE_M = 2.4;

/**
 * These source walls and railing describe the sides of the sunken Geležinio
 * Vilko approach. Until the terrain carries that vertical cut, rendering them
 * as generic terrain-following structures raises false barriers across the
 * flat carriageway.
 */
export const GELEZINIO_VILKO_SUPPRESSED_GENERIC_STRUCTURE_IDS = Object.freeze(
  new Set([
    "osm:way:290171620",
    "osm:way:290171623",
    "osm:way:441711582",
  ]),
);

/**
 * OSM way 290171625 traces the two useful tunnel-edge railings as one open
 * polyline, but joins them with a middle segment across both carriageways.
 * That connecting segment is source topology, not a physical gate. Keep the
 * two side runs and omit only the cross-mouth segment from render and physics.
 */
const GELEZINIO_VILKO_SUPPRESSED_GENERIC_STRUCTURE_SEGMENTS = Object.freeze(
  new Map<string, ReadonlySet<number>>([
    ["osm:way:290171625", Object.freeze(new Set([1]))],
  ]),
);

function genericStructureSourceId(runtimeFeatureId: string): string {
  const partSuffix = runtimeFeatureId.indexOf("#");
  return partSuffix === -1
    ? runtimeFeatureId
    : runtimeFeatureId.slice(0, partSuffix);
}

export function isSuppressedGelezinioVilkoGenericStructureId(
  runtimeFeatureId: string,
): boolean {
  return GELEZINIO_VILKO_SUPPRESSED_GENERIC_STRUCTURE_IDS.has(
    genericStructureSourceId(runtimeFeatureId),
  );
}

/**
 * OSM way 531487452 is the Palace of the Grand Dukes outline exported as a
 * 22 m generic wall. The authored palace asset replaces that outline with its
 * surveyed facade and enterable south range, so retaining the generic wall
 * would duplicate the exterior and cut across the museum interior.
 */
export const AUTHORED_LANDMARK_SUPPRESSED_GENERIC_STRUCTURE_IDS =
  Object.freeze(new Set([
    "osm:way:531487452",
  ]));

export function isSuppressedAuthoredGenericStructureId(
  runtimeFeatureId: string,
): boolean {
  const sourceId = genericStructureSourceId(runtimeFeatureId);
  return GELEZINIO_VILKO_SUPPRESSED_GENERIC_STRUCTURE_IDS.has(sourceId) ||
    AUTHORED_LANDMARK_SUPPRESSED_GENERIC_STRUCTURE_IDS.has(sourceId);
}

export function isSuppressedGelezinioVilkoGenericStructureSegment(
  runtimeFeatureId: string,
  segmentIndex: number,
): boolean {
  return GELEZINIO_VILKO_SUPPRESSED_GENERIC_STRUCTURE_SEGMENTS
    .get(genericStructureSourceId(runtimeFeatureId))
    ?.has(segmentIndex) ?? false;
}

const GELEZINIO_VILKO_TUNNEL_BUILDING_ID =
  "grpk:7fc9802f-b671-44a0-bc6b-cb707174157d";

/**
 * The official city description places the Geležinio Vilko tunnel beneath
 * Nepriklausomybės aikštė, Gedimino pr. and A. Tumėno g. GRPK supplies the
 * overhead footprint and the two carriageway centrelines separately, so the
 * authored passages restore their missing vertical relationship without
 * changing either source feature.
 *
 * https://vilnius.lt/naujienos/projektuotojai-pristatys-gelezinio-vilko-gatves-tunelio-remonto-projekta
 */
export const GELEZINIO_VILKO_BUILDING_PASSAGES: readonly Readonly<
  AuthoredBuildingPassageSpec
>[] = Object.freeze([
  Object.freeze({
    id: "gelezinio-vilko-tunnel-west",
    tileId: "-4:-1",
    buildingId: GELEZINIO_VILKO_TUNNEL_BUILDING_ID,
    centreLineLocalM: Object.freeze({
      from: Object.freeze({ x: 19.786, z: 94.585 }),
      to: Object.freeze({ x: 17.012, z: 127.001 }),
    }),
    widthM: 10.2,
    ceilingOffsetFromBuildingBaseM: 4.4,
  }),
  Object.freeze({
    id: "gelezinio-vilko-tunnel-east",
    tileId: "-4:-1",
    buildingId: GELEZINIO_VILKO_TUNNEL_BUILDING_ID,
    centreLineLocalM: Object.freeze({
      from: Object.freeze({ x: 32.781, z: 98.008 }),
      to: Object.freeze({ x: 30.665, z: 128.802 }),
    }),
    widthM: 10.2,
    ceilingOffsetFromBuildingBaseM: 4.4,
  }),
]);

export const OLD_TOWN_COURTYARD_PASSAGES: readonly Readonly<
  AuthoredBuildingPassageSpec
>[] = Object.freeze([
  Object.freeze({
    id: "love-arch-vokieciu-22",
    tileId: "1:3",
    buildingId: "grpk:33522556-08be-405c-b7da-3ad5be6dd1e5",
    centreLineLocalM: Object.freeze({
      from: Object.freeze({ x: 124, z: 234 }),
      to: Object.freeze({ x: 65, z: 214 }),
    }),
    widthM: 2.15,
    ceilingOffsetFromBuildingBaseM: 2.75,
  }),
  Object.freeze({
    id: "love-arch-vokieciu-22-east-cap",
    tileId: "1:3",
    buildingId: "grpk:82f15e2e-f788-40bd-a08c-52ff2e7b9b08",
    centreLineLocalM: Object.freeze({
      from: Object.freeze({ x: 124, z: 234 }),
      to: Object.freeze({ x: 65, z: 214 }),
    }),
    widthM: 2.15,
    ceilingOffsetFromBuildingBaseM: 2.75,
  }),
  Object.freeze({
    id: "love-arch-vokieciu-22-east-wing",
    tileId: "1:3",
    buildingId: "grpk:AA565FD0-81BA-4A9D-9963-7AD44320E249",
    centreLineLocalM: Object.freeze({
      from: Object.freeze({ x: 124, z: 234 }),
      to: Object.freeze({ x: 65, z: 214 }),
    }),
    widthM: 2.15,
    ceilingOffsetFromBuildingBaseM: 2.75,
  }),
  Object.freeze({
    id: "love-arch-vokieciu-22-west-wing",
    tileId: "1:3",
    buildingId: "grpk:c9b1e648-9fd9-4a3a-a537-9dec32a89b0f",
    centreLineLocalM: Object.freeze({
      from: Object.freeze({ x: 124, z: 234 }),
      to: Object.freeze({ x: 65, z: 214 }),
    }),
    widthM: 2.15,
    ceilingOffsetFromBuildingBaseM: 2.75,
  }),
  Object.freeze({
    id: "courtyard-key-blue-gate-passage",
    tileId: "1:1",
    buildingId: "grpk:3516B4CF-1162-40BC-BF0E-8BE5F50C18FA",
    centreLineLocalM: Object.freeze({
      from: Object.freeze({ x: 256, z: 113.508526 }),
      to: Object.freeze({ x: 240.72875, z: 153.58 }),
    }),
    widthM: 2.8,
    ceilingOffsetFromBuildingBaseM: 3.35,
    destinationHoleIndex: 0,
  }),
]);

/**
 * OSM way 8020532 records Aušros Vartų g. as a building passage through the
 * Gate of Dawn and joins a 3.4 m paving-stone living street on either side.
 * The authoritative GRPK building footprint is owned by tile 3:5 even though
 * its southern end crosses the tile boundary, so the local coordinates below
 * deliberately extend beyond 256 m.
 */
export const GATE_OF_DAWN_BUILDING_PASSAGES: readonly Readonly<
  AuthoredBuildingPassageSpec
>[] = Object.freeze([
  Object.freeze({
    id: "gate-of-dawn-ausros-vartu",
    tileId: "3:5",
    buildingId: "grpk:58f353a1-513e-45bd-9105-1158dca6478b",
    centreLineLocalM: Object.freeze({
      from: Object.freeze({ x: 5.92, z: 303.77 }),
      to: Object.freeze({ x: -4.69, z: 243.66 }),
    }),
    widthM: 3.4,
    ceilingOffsetFromBuildingBaseM: 7.65,
  }),
  Object.freeze({
    id: "gate-of-dawn-pilgrim-stair-entry",
    tileId: "2:6",
    buildingId: "grpk:0b7e5d1d-19f0-45ab-ae0e-2e362eaee6a0",
    centreLineLocalM: Object.freeze({
      from: Object.freeze({ x: 253.377, z: 26.856 }),
      to: Object.freeze({ x: 249.867, z: 7.08 }),
    }),
    widthM: 4.0,
    ceilingOffsetFromBuildingBaseM: 8.2,
  }),
  Object.freeze({
    id: "gate-of-dawn-upper-stair-clearance",
    tileId: "2:6",
    buildingId: "grpk:ed1fefad-2be6-4cee-85fe-eb67111488b4",
    centreLineLocalM: Object.freeze({
      from: Object.freeze({ x: 252.852, z: 11.656 }),
      to: Object.freeze({ x: 248.951, z: 12.543 }),
    }),
    widthM: 5.0,
    ceilingOffsetFromBuildingBaseM: 6.4,
  }),
  Object.freeze({
    id: "gate-of-dawn-upper-stair-north-edge-clearance",
    tileId: "2:6",
    buildingId: "grpk:ed1fefad-2be6-4cee-85fe-eb67111488b4",
    centreLineLocalM: Object.freeze({
      from: Object.freeze({ x: 250.0, z: 16.5 }),
      to: Object.freeze({ x: 250.0, z: 13.0 }),
    }),
    widthM: 4.0,
    ceilingOffsetFromBuildingBaseM: 6.4,
  }),
]);

/**
 * The custom Pilies and Didžioji GLBs contain these same open arches. Keeping
 * the GRPK wall-ring openings in domain config makes the visible courtyard
 * entrances physically traversable without importing render-engine geometry
 * into simulation or physics.
 */
type GeneratedPassageValue = readonly [
  string,
  string,
  WorldTileId,
  string,
  Readonly<{ x: number; z: number }>,
  Readonly<{ x: number; z: number }>,
  number,
  number,
  number | null,
  boolean,
];

function authoredPassagesFromGeneratedValues(
  values: readonly GeneratedPassageValue[],
): readonly Readonly<AuthoredBuildingPassageSpec>[] {
  return Object.freeze(values.map(([
    id,
    visiblePassageId,
    tileId,
    buildingId,
    from,
    to,
    widthM,
    ceilingOffsetFromBuildingBaseM,
    destinationHoleIndex,
    supplementalSharedWall,
  ]) => Object.freeze({
    id,
    visiblePassageId,
    tileId,
    buildingId,
    centreLineLocalM: Object.freeze({
      from: Object.freeze({ ...from }),
      to: Object.freeze({ ...to }),
    }),
    widthM,
    ceilingOffsetFromBuildingBaseM,
    ...(destinationHoleIndex === null
      ? {}
      : { destinationHoleIndex }),
    ...(supplementalSharedWall
      ? { supplementalSharedWall: true }
      : {}),
  })));
}

export const RULERS_WAY_COURTYARD_PASSAGES: readonly Readonly<
  AuthoredBuildingPassageSpec
>[] = authoredPassagesFromGeneratedValues(
  RULERS_WAY_COURTYARD_PASSAGE_VALUES,
);

export const GLASS_VILNIAUS_COURTYARD_PASSAGES: readonly Readonly<
  AuthoredBuildingPassageSpec
>[] = authoredPassagesFromGeneratedValues(
  GLASS_VILNIAUS_COURTYARD_PASSAGE_VALUES,
);

export const AUTHORED_BUILDING_PASSAGES: readonly Readonly<
  AuthoredBuildingPassageSpec
>[] = Object.freeze([
  ...GELEZINIO_VILKO_BUILDING_PASSAGES,
  ...OLD_TOWN_COURTYARD_PASSAGES,
  ...GATE_OF_DAWN_BUILDING_PASSAGES,
  ...RULERS_WAY_COURTYARD_PASSAGES,
  ...GLASS_VILNIAUS_COURTYARD_PASSAGES,
]);

export function authoredBuildingPassagesForBuilding(
  tileId: string,
  buildingId: string,
): readonly Readonly<AuthoredBuildingPassageSpec>[] {
  const normalizedBuildingId = buildingId.toLowerCase();
  return AUTHORED_BUILDING_PASSAGES.filter((passage) =>
    passage.tileId === tileId &&
    passage.buildingId.toLowerCase() === normalizedBuildingId
  );
}
